'use client';

import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  type CardData,
  type CardStatus,
  generateRandomPolygonAddress,
  type KycStatus,
  mockBalances,
  mockCards,
  mockMovements,
  mockPolygonAccounts,
  mockUser,
  type Movement,
  type PolygonAccount,
  type User,
  type WalletBalances,
} from '../lib/mock-data';

interface WalletContextType {
  user: User | null;
  balances: WalletBalances;
  movements: Movement[];
  // Multi-card
  cards: CardData[];
  activeCardId: string | null;
  setActiveCardId: (id: string | null) => void;
  addCard: (label: string) => void;
  toggleCardFreeze: (cardId: string) => void;
  // Polygon accounts
  polygonAccounts: PolygonAccount[];
  totalKSM: number;
  addPolygonAccount: (label: string) => void;
  removePolygonAccount: (id: string) => void;
  // Auth & misc
  isLoggedIn: boolean;
  showBalances: boolean;
  setShowBalances: (show: boolean) => void;
  login: (method: 'email' | 'phone') => void;
  logout: () => void;
  setKycStatus: (status: KycStatus) => void;
  setCardStatus: (status: CardStatus) => void;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

let nextCardNum = 3;
let nextPolyNum = 3;

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(mockUser);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [showBalances, setShowBalances] = useState(true);
  const [balances] = useState<WalletBalances>(mockBalances);
  const [movements] = useState<Movement[]>(mockMovements);

  // Multi-card state
  const [cards, setCards] = useState<CardData[]>(mockCards);
  const [activeCardId, setActiveCardId] = useState<string | null>(
    mockCards[0]?.id ?? null,
  );

  // Polygon accounts state
  const [polygonAccounts, setPolygonAccounts] =
    useState<PolygonAccount[]>(mockPolygonAccounts);

  const totalKSM = useMemo(
    () => polygonAccounts.reduce((sum, a) => sum + a.balance, 0),
    [polygonAccounts],
  );

  const login = useCallback((method: 'email' | 'phone') => {
    setUser({
      ...mockUser,
      loginMethod: method,
      maskedContact: method === 'email' ? 'j***@gmail.com' : '+57 *** *** 4521',
    });
    setIsLoggedIn(true);
  }, []);

  const logout = useCallback(() => {
    setIsLoggedIn(false);
    setUser(null);
  }, []);

  const setKycStatus = useCallback((status: KycStatus) => {
    setUser((prev) => (prev ? { ...prev, kycStatus: status } : null));
  }, []);

  const setCardStatus = useCallback(
    (status: CardStatus) => {
      // Legacy helper kept for existing callers.
      if (status === 'none') {
        setCards([]);
        setActiveCardId(null);
        return;
      }

      const targetId = activeCardId ?? cards[0]?.id;
      if (!targetId && status === 'active') {
        const newCard: CardData = {
          id: `card_${String(nextCardNum++).padStart(3, '0')}`,
          label: 'Personal',
          status: 'active',
          last4: String(Math.floor(1000 + Math.random() * 9000)),
          frozen: false,
          limits: { daily: 500, monthly: 5000 },
          createdAt: new Date().toISOString(),
        };
        setCards([newCard]);
        setActiveCardId(newCard.id);
        return;
      }

      if (!targetId) return;

      setCards((prev) =>
        prev.map((card) =>
          card.id === targetId
            ? {
                ...card,
                status,
                frozen: status === 'frozen',
              }
            : card,
        ),
      );
    },
    [activeCardId, cards],
  );

  const addCard = useCallback((label: string) => {
    const cardNumber = nextCardNum++;
    const newCard: CardData = {
      id: `card_${String(cardNumber).padStart(3, '0')}`,
      label: label.trim() || `Tarjeta ${cardNumber}`,
      status: 'active',
      last4: String(Math.floor(1000 + Math.random() * 9000)),
      frozen: false,
      limits: { daily: 500, monthly: 5000 },
      createdAt: new Date().toISOString(),
    };
    setCards((prev) => [...prev, newCard]);
    setActiveCardId(newCard.id);
  }, [cards.length]);

  const toggleCardFreeze = useCallback((cardId: string) => {
    setCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? {
              ...card,
              frozen: !card.frozen,
              status: card.frozen ? 'active' : 'frozen',
            }
          : card,
      ),
    );
  }, []);

  const addPolygonAccount = useCallback((label: string) => {
    const accountNumber = nextPolyNum++;
    const newAccount: PolygonAccount = {
      id: `poly_${String(accountNumber).padStart(3, '0')}`,
      label: label.trim() || `Cuenta ${accountNumber}`,
      address: generateRandomPolygonAddress(),
      balance: 0,
    };
    setPolygonAccounts((prev) => [...prev, newAccount]);
  }, []);

  const removePolygonAccount = useCallback(
    (id: string) => {
      setPolygonAccounts((prev) => {
        const account = prev.find((item) => item.id === id);
        if (!account) return prev;

        if (account.balance > 0) {
          const shouldRemove = window.confirm(
            `La cuenta ${account.label} tiene ${account.balance.toFixed(
              4,
            )} KSM. ¿Deseas eliminarla de todas formas?`,
          );
          if (!shouldRemove) return prev;
        }

        return prev.filter((item) => item.id !== id);
      });
    },
    [setPolygonAccounts],
  );

  return (
    <WalletContext.Provider
      value={{
        user,
        balances,
        movements,
        cards,
        activeCardId,
        setActiveCardId,
        addCard,
        toggleCardFreeze,
        polygonAccounts,
        totalKSM,
        addPolygonAccount,
        removePolygonAccount,
        isLoggedIn,
        showBalances,
        setShowBalances,
        login,
        logout,
        setKycStatus,
        setCardStatus,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}
